import React from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const TaskCard = ({
  // Task data
  task,
  taskKey,
  category,
  index,

  // State
  isSelected,
  isCollapsed,
  isInjected = false,
  isCustom = false,

  // Data values
  customName,
  instructions,
  tools,
  notes,

  // Event handlers
  onToggle,
  onCollapse,
  onDataChange,
  onRemove,
  onAddAnother,

  // Additional props
  dragHandlers = {},
  originalContact = null,
}) => {
  const isDraggable = true;

  return (
    <div
      key={taskKey}
      className={`p-4 border rounded-lg transition-all text-text-primary ${
        isSelected
          ? 'bg-surface-muted border-primary/40 shadow-sm cursor-move'
          : 'bg-surface border-border hover:bg-surface-muted cursor-move'
      } ${isInjected ? 'ml-6 border-l-4 border-primary' : ''}`}
      draggable={isDraggable}
      onDragStart={dragHandlers.onDragStart}
      onDragEnd={dragHandlers.onDragEnd}
      onDragOver={dragHandlers.onDragOver}
      onDrop={dragHandlers.onDrop}
      title="Drag to reorder this task"
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          {/* Drag handle */}
          <div
            className="flex flex-col opacity-40 hover:opacity-60 transition-opacity mt-1 cursor-move"
            title="Drag to reorder"
          >
            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mb-0.5"></div>
            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mb-0.5"></div>
            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
          </div>

          <Checkbox id={taskKey} checked={isSelected} onCheckedChange={onToggle} className="mt-1" />

          <div className="flex-1 min-w-0">
            <Label
              htmlFor={taskKey}
              className="text-sm font-medium text-text-primary cursor-pointer flex items-center gap-2"
            >
              <span className="text-text-primary">{task.name}</span>

              {/* Completion Status Indicators */}
              <div className="flex items-center gap-1">
                {instructions && instructions.trim() !== '' && (
                  <span
                    className="w-2 h-2 bg-purple-500 rounded-full"
                    title="Instructions added"
                  ></span>
                )}
                {tools && tools.trim() !== '' && (
                  <span
                    className="w-2 h-2 bg-orange-500 rounded-full"
                    title="Tools & supplies listed"
                  ></span>
                )}
                {instructions && instructions.trim() !== '' && tools && tools.trim() !== '' && (
                  <span className="text-xs text-primary font-medium">✓ Ready</span>
                )}
              </div>

              {isSelected && !isCollapsed && (
                <span className="ml-auto text-xs text-theme-accent font-normal">(selected)</span>
              )}
              {isSelected && isCollapsed && (
                <span className="ml-auto text-xs text-text-muted font-normal">
                  (selected & collapsed)
                </span>
              )}
            </Label>
            {task.subcategory && (
              <Badge variant={isInjected ? 'secondary' : 'outline'} className="mt-1">
                {isInjected
                  ? `Additional ${originalContact?.subcategory || task.subcategory}`
                  : task.subcategory}
              </Badge>
            )}
          </div>

          {/* Collapse/Expand button for selected tasks */}
          {isSelected && (
            <Button
              onClick={onCollapse}
              variant="ghost"
              size="sm"
              className="p-1 h-8 w-8 text-text-muted hover:text-text-primary"
              title={isCollapsed ? 'Expand task details' : 'Collapse task details'}
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* Remove button for injected/custom tasks */}
          {(isInjected || isCustom) && onRemove && (
            <Button
              onClick={onRemove}
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10 p-1 h-8 w-8"
              title="Remove this task"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Task details form - only show if selected and not collapsed */}
        {isSelected && !isCollapsed && (
          <>
            <div className="mb-3">
              <Label className="text-xs text-text-muted">Custom Task Name (Optional)</Label>
              <input
                type="text"
                value={customName || ''}
                onChange={(e) => onDataChange('customName', e.target.value)}
                placeholder={`e.g., "${task.name}" or customize it`}
                className="mt-1 h-8 text-sm w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground"
              />
              <p className="text-xs text-text-muted mt-1">
                Leave blank to use default task name: "{task.name}"
              </p>
            </div>

            <div className="mb-3">
              <Label className="text-xs text-text-muted flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Instructions
              </Label>
              <Textarea
                value={instructions || ''}
                onChange={(e) => onDataChange('address', e.target.value)}
                placeholder="Enter step-by-step maintenance instructions"
                className="mt-1 text-sm"
                rows={3}
              />
            </div>

            <div className="mb-3">
              <Label className="text-xs text-text-muted flex items-center gap-1">
                🔧 Required Tools & Supplies
                {tools && tools.includes('•') && (
                  <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                    Auto-filled ✨
                  </span>
                )}
              </Label>
              <Textarea
                value={tools || ''}
                onChange={(e) => onDataChange('tools', e.target.value)}
                placeholder="List required tools, materials, and supplies (e.g., screwdriver, filter, cleaner)"
                className="mt-1 text-sm"
                rows={3}
              />
              {!tools || tools.trim() === '' ? (
                <p className="text-xs text-text-muted mt-1">
                  💡 Tools will be auto-suggested when you select this task
                </p>
              ) : null}
            </div>

            <div className="mb-3">
              <Label className="text-xs text-text-muted">Completion Notes & Status</Label>
              <Textarea
                value={notes || ''}
                onChange={(e) => onDataChange('notes', e.target.value)}
                placeholder="Track completion status, dates, observations, or additional notes"
                className="mt-1 text-sm"
                rows={2}
              />
            </div>

            {/* Add Another Button - only for original tasks */}
            {!isInjected && !isCustom && onAddAnother && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button
                  onClick={onAddAnother}
                  variant="outline"
                  size="sm"
                  className="w-full flex items-center gap-2 text-xs"
                >
                  <Plus className="h-3 w-3" />
                  Add Another "{task.name}"
                </Button>
                <p className="text-xs text-text-muted mt-1 text-center">
                  Will appear directly below this task
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
